import React, {
	useState,
	useReducer,
	useCallback,
	useRef,
	useEffect,
} from "react";
import {
	Grid,
	Typography,
	Box,
	Button,
	TextField,
	MenuItem,
	Checkbox,
	FormControlLabel,
	Paper,
	Alert,
	CircularProgress,
	Backdrop,
} from "@mui/material";
import axios from "../../../api/axios";
import ShipmentInfoModal from "../../../admin_dashboard/orders/create-shipment-ui/ShipmentInfoModal";
import NorthIcon from "@mui/icons-material/North";
import BusinessIcon from "@mui/icons-material/Business";

// --------- Shipment reducer & initial state ---------
const initialShipments = [
	{
		itemWeight: "",
		itemLength: "",
		itemWidth: "",
		itemHeight: "",
		itemQuantity: "",
		itemValue: "",
		itemDescription: "", // will hold selected value or 'Custom'
		customDescription: "", // holds custom description if 'Custom' is selected
		nonStacked: false,
		isFragile: false,
	},
];

function shipmentsReducer(state, action) {
	switch (action.type) {
		case "ADD_SHIPMENT":
			return [...state, { ...initialShipments[0] }];
		case "UPDATE_SHIPMENT":
			const { index, field, value } = action.payload;
			return state.map((item, i) =>
				i === index ? { ...item, [field]: value } : item
			);
		case "REMOVE_SHIPMENT":
			const newState = [...state];
			if (newState.length > 1) {
				newState.splice(action.payload.index, 1);
			}
			return newState;
		case "RESET":
			return initialShipments;
		default:
			return state;
	}
}

// --------- Loader component with Backdrop ---------
function Loader({ open }) {
	return (
		<Backdrop
			open={open}
			style={{
				zIndex: 9999,
				color: "#fff",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}>
			<CircularProgress size={60} color="inherit" />
		</Backdrop>
	);
}

export default function BusinessExportQuote() {
	const itemDescriptionOptions = [
		{ label: "Electronics", value: "Electronics" },
		{ label: "Documents", value: "Documents" },
		{ label: "Clothing", value: "Clothing" },
		{ label: "Other", value: "Custom" },
	];

	const [formData, setFormData] = useState({
		businessName: "",
		businessEmail: "",
		receiverName: "",
		receiverEmail: "",
		fromCountry: "Nigeria",
		fromCity: "",
		fromCityPostalCode: "",
		toCountry: "China",
		toCity: "",
		toCityPostalCode: "",
	});
	const [shipments, dispatchShipments] = useReducer(
		shipmentsReducer,
		initialShipments
	);
	const [shipmentData, setShipmentData] = useState(null);
	const [open, setOpen] = useState(false);
	const [backendError, setBackendError] = useState("");
	const [loading, setLoading] = useState(false);
	const [loaderOpen, setLoaderOpen] = useState(false); // For overlay spinner
	const [isImport, setIsImport] = useState();

	// Refs for focusing inputs
	const inputRefs = useRef([]);

	// Helper to generate unique index for refs
	const setRef = (index, field, ref) => {
		inputRefs.current[index * 10 + field] = ref;
	};

	// Focus specific input
	const focusInput = (index, field) => {
		const ref = inputRefs.current[index * 10 + field];
		if (ref) {
			ref.focus();
		}
	};

	// When adding a new shipment, auto-focus on itemWeight
	const handleAddShipment = useCallback(() => {
		dispatchShipments({ type: "ADD_SHIPMENT" });
		setTimeout(() => {
			const newIndex = shipments.length; // new last index
			focusInput(newIndex, 0); // focus itemWeight
		}, 100);
	}, [shipments.length]);

	const handleMainChange = useCallback((e) => {
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	}, []);

	const handleShipmentChange = useCallback(
		(index, field) => (e) => {
			const value =
				e.target.type === "checkbox" ? e.target.checked : e.target.value;

			dispatchShipments({
				type: "UPDATE_SHIPMENT",
				payload: {
					index,
					field,
					value,
				},
			});
		},
		[]
	);

	const handleRemoveShipment = useCallback((index) => {
		dispatchShipments({ type: "REMOVE_SHIPMENT", payload: { index } });
	}, []);

	const handleToggleChange = useCallback(() => {
		setIsImport((prev) => !prev);
	}, []);

	const handleOpen = useCallback(() => setOpen(true), []);
	const handleClose = useCallback(() => setOpen(false), []);

	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			setBackendError("");
			setLoaderOpen(true);
			setLoading(true);
			try {
				const response = await axios.post("/create-business-export-shipment", {
					...formData,
					shipments,
					isPersonal: false,
					isBusiness: true,
					isImport: false,
					isExport: true,
				});
				if (response.status === 201) {
					setShipmentData(response.data.message);
					handleOpen();
				}
			} catch (error) {
				console.error("Submission error:", error);
				setBackendError(
					error.response?.data?.message || error.message || "Error occurred"
				);
			} finally {
				setLoading(false);
				setLoaderOpen(false);
			}
		},
		[formData, shipments, handleOpen]
	);

	// Shipment Item component
	const ShipmentItem = React.memo(({ shipment, index, onRemove }) => {
		const refs = [
			useRef(null), // itemWeight
			useRef(null), // itemLength
			useRef(null), // itemWidth
			useRef(null), // itemHeight
			useRef(null), // itemValue
			useRef(null), // itemDescription (select)
			useRef(null), // itemQuantity
		];

		// Register refs
		useEffect(() => {
			refs.forEach((ref, i) => {
				setRef(index, i, ref.current);
			});
		}, [index]);

		// Determine if "Custom" is selected
		const isCustomDescription = shipment.itemDescription === "Custom";

		return (
			<Box
				sx={{
					border: "1px solid #ccc",
					borderRadius: 2,
					p: 2,
					mb: 2,
					backgroundColor: "#f9f9f9",
				}}>
				{/* Header */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}>
					<Typography
						sx={{
							bgcolor: "#333",
							color: "white",
							p: 0.5,
							borderRadius: 1,
							mb: 2,
							fontSize: { xs: "0.8rem", sm: "1rem" },
						}}>
						Shipment {index + 1}
					</Typography>
					<Button
						variant="outlined"
						color="error"
						size="small"
						onClick={() => onRemove(index)}>
						Cancel
					</Button>
				</Box>
				{/* Shipment details */}
				<Grid container spacing={2} alignItems="center">
					{/* Item Weight */}
					<Grid item xs={12} sm={6}>
						<TextField
							label="Item Weight (kg)"
							type="number"
							fullWidth
							inputRef={refs[0]}
							onFocus={() => focusInput(index, 0)}
							onChange={handleShipmentChange(index, "itemWeight")}
							value={shipment.itemWeight}
							required
						/>
					</Grid>
					{/* Length, Width, Height */}
					{["itemLength", "itemWidth", "itemHeight"].map((field, i) => (
						<Grid item xs={12} sm={4} key={field}>
							<TextField
								label={field.replace("item", "").replace(/([A-Z])/g, " $1")}
								type="number"
								fullWidth
								inputRef={refs[i + 1]}
								onFocus={() => focusInput(index, i + 1)}
								onChange={handleShipmentChange(index, field)}
								value={shipment[field]}
								required
							/>
						</Grid>
					))}
					{/* Item Value */}
					<Grid item xs={12} sm={6}>
						<TextField
							label="Item Value in USD"
							type="number"
							fullWidth
							inputRef={refs[4]}
							onFocus={() => focusInput(index, 4)}
							onChange={handleShipmentChange(index, "itemValue")}
							value={shipment.itemValue}
							required
						/>
					</Grid>
					{/* Item Description Dropdown */}
					<Grid item xs={12}>
						<TextField
							select
							label="Item Description"
							fullWidth
							sx={{ minWidth: "10rem" }}
							value={shipment.itemDescription}
							onChange={(e) => {
								let value = e.target.value;
								// Clear customDescription if not "Custom"
								if (value !== "Custom") {
									dispatchShipments({
										type: "UPDATE_SHIPMENT",
										payload: {
											index,
											field: "itemDescription",
											value,
										},
									});
								} else {
									dispatchShipments({
										type: "UPDATE_SHIPMENT",
										payload: {
											index,
											field: "itemDescription",
											value,
										},
									});
								}
							}}
							inputRef={refs[5]}>
							{itemDescriptionOptions.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					{/* Show custom description input if "Custom" is selected */}
					{shipment.itemDescription === "Custom" && (
						<Grid item xs={12}>
							<TextField
								label="Custom Description"
								placeholder="Enter custom description"
								fullWidth
								value={shipment.customDescription || ""}
								onChange={(e) =>
									dispatchShipments({
										type: "UPDATE_SHIPMENT",
										payload: {
											index,
											field: "customDescription",
											value: e.target.value,
										},
									})
								}
							/>
						</Grid>
					)}
					{/* Item Quantity */}
					<Grid item xs={12} sm={6}>
						<TextField
							label="Item Quantity"
							type="number"
							fullWidth
							inputRef={refs[6]}
							onFocus={() => focusInput(index, 6)}
							onChange={handleShipmentChange(index, "itemQuantity")}
							value={shipment.itemQuantity}
							required
						/>
					</Grid>
					{/* Checkboxes */}
					<Grid item xs={12} sm={6} display="flex" gap={1} alignItems="center">
						<FormControlLabel
							control={
								<Checkbox
									checked={shipment.nonStacked}
									onChange={handleShipmentChange(index, "nonStacked")}
									color="primary"
								/>
							}
							label="Non-stacked"
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={shipment.isFragile}
									onChange={handleShipmentChange(index, "isFragile")}
									color="primary"
								/>
							}
							label="Fragile"
						/>
					</Grid>
				</Grid>
			</Box>
		);
	});

	return (
		<Box sx={{ maxWidth: 1200, mx: "auto", px: 2, my: 4 }}>
			{/* Error Alert */}
			{backendError && (
				<Box mb={2}>
					<Alert severity="error">{backendError}</Alert>
				</Box>
			)}

			{/* Overlay Loader */}
			<Loader open={loaderOpen} />

			{/* Shipment Info Modal */}
			{shipmentData && (
				<ShipmentInfoModal
					open={open}
					handleClose={handleClose}
					shipment={shipmentData}
				/>
			)}

			{/* Page Title */}
			<Typography
				variant="h4"
				align="center"
				sx={{
					mb: 3,
					p: 2,
					bgcolor: "#034052",
					color: "white",
					borderRadius: 2,
					fontSize: { xs: "1rem", md: "1.2rem" },
					fontWeight: "bold",
				}}>
				<NorthIcon /> <BusinessIcon /> Business Export
			</Typography>

			{/* Main Form */}
			<Box
				component="form"
				onSubmit={handleSubmit}
				sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{/* Sender & Recipient Details */}
				<Paper
					sx={{
						p: 2,
						mb: 3,
						borderRadius: 2,
						backgroundColor: "#fff",
						boxShadow: 1,
					}}>
					<Typography
						variant="h6"
						sx={{
							mb: 2,
							fontSize: { xs: "0.9rem", md: "1rem" },
							fontWeight: "bold",
						}}>
						⥯ Sender & Recipient Details:
					</Typography>
					<Grid container spacing={2}>
						{/* Business Name */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Business Name"
								name="businessName"
								fullWidth
								required
								value={formData.businessName}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* Business Email */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Business Email"
								name="businessEmail"
								type="email"
								fullWidth
								required
								value={formData.businessEmail}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* Receiver Name */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Receiver's Name"
								name="receiverName"
								fullWidth
								required
								value={formData.receiverName}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* Receiver Email */}
						<Grid item xs={12} sm={6}>
							<TextField
								label="Receiver's Email"
								name="receiverEmail"
								type="email"
								fullWidth
								required
								value={formData.receiverEmail}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* From Country */}
						<Grid item xs={12} sm={4}>
							<TextField
								select
								label="Shipping From Country"
								value={formData.fromCountry}
								onChange={(e) =>
									handleMainChange({
										target: { name: "fromCountry", value: e.target.value },
									})
								}
								fullWidth>
								{[
									{ value: "Nigeria", label: "Nigeria", code: "NG" },
									{ value: "France", label: "France", code: "FR" },
								].map((option) => (
									<MenuItem
										key={option.value}
										value={option.value}
										sx={{ display: "flex", alignItems: "center" }}>
										<img
											src={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png`}
											alt={option.label}
											width={40}
											height={30}
											style={{ marginRight: 8 }}
										/>
										<Typography>{option.label}</Typography>
									</MenuItem>
								))}
							</TextField>
						</Grid>
						{/* From City */}
						<Grid item xs={12} sm={4}>
							<TextField
								label="From City"
								name="fromCity"
								fullWidth
								required
								value={formData.fromCity}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* From Postal Code */}
						<Grid item xs={12} sm={4}>
							<TextField
								label="From Postal Code"
								name="fromCityPostalCode"
								fullWidth
								required
								value={formData.fromCityPostalCode}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* To Country */}
						<Grid item xs={12} sm={4}>
							<TextField
								select
								label="Destination Country"
								value={formData.toCountry}
								onChange={(e) =>
									handleMainChange({
										target: { name: "toCountry", value: e.target.value },
									})
								}
								fullWidth>
								<MenuItem
									value="China"
									sx={{ display: "flex", alignItems: "center" }}>
									<img
										src={`https://flagcdn.com/w40/cn.png`}
										alt="China"
										width={40}
										height={30}
										style={{ marginRight: 8 }}
									/>
									<Typography>China</Typography>
								</MenuItem>
							</TextField>
						</Grid>
						{/* To City */}
						<Grid item xs={12} sm={4}>
							<TextField
								label="To City"
								name="toCity"
								fullWidth
								required
								value={formData.toCity}
								onChange={handleMainChange}
							/>
						</Grid>
						{/* To Postal Code */}
						<Grid item xs={12} sm={4}>
							<TextField
								label="To Postal Code"
								name="toCityPostalCode"
								fullWidth
								required
								value={formData.toCityPostalCode}
								onChange={handleMainChange}
							/>
						</Grid>
					</Grid>
				</Paper>

				{/* Shipment Details */}
				<Paper
					sx={{
						p: 2,
						mb: 3,
						borderRadius: 2,
						backgroundColor: "#f9f9f9",
						boxShadow: 1,
					}}>
					<Typography
						variant="h6"
						sx={{
							mb: 2,
							fontSize: { xs: "0.9rem", md: "1rem" },
							bgcolor: "#034052",
							color: "yellow",
							p: 1,
							borderRadius: 1,
							fontWeight: "bold",
						}}>
						❒ Shipment Details
					</Typography>
					{shipments.map((shipment, index) => (
						<ShipmentItem
							key={index}
							shipment={shipment}
							index={index}
							onRemove={handleRemoveShipment}
						/>
					))}
					{/* Buttons */}
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}>
						<Button
							variant="contained"
							color="success"
							onClick={handleAddShipment}
							sx={{ minWidth: 150 }}>
							Add Shipment
						</Button>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							disabled={loading}
							sx={{ minWidth: 150, position: "relative" }}>
							{loading ? "Processing..." : "Create Shipment"}
						</Button>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
}
